import publicDomainManifest from "../generated/public-domain-versions.json";
import holybooksManifest from "../generated/holybooks-versions.json";

export const bibleVersionIds = ["kjv", "esv", "web", "bsb", "asv", "cpdv", "erv", "amp", "nasb"] as const;
export type BibleVersionId = (typeof bibleVersionIds)[number];
export type BibleAudioKind = "public-domain" | "generated";

export const bibleBooks = [
  "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy", "Joshua",
  "Judges", "Ruth", "1 Samuel", "2 Samuel", "1 Kings", "2 Kings",
  "1 Chronicles", "2 Chronicles", "Ezra", "Nehemiah", "Esther", "Job",
  "Psalms", "Proverbs", "Ecclesiastes", "Song of Solomon", "Isaiah",
  "Jeremiah", "Lamentations", "Ezekiel", "Daniel", "Hosea", "Joel", "Amos",
  "Obadiah", "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah", "Haggai",
  "Zechariah", "Malachi", "Matthew", "Mark", "Luke", "John", "Acts", "Romans",
  "1 Corinthians", "2 Corinthians", "Galatians", "Ephesians", "Philippians",
  "Colossians", "1 Thessalonians", "2 Thessalonians", "1 Timothy",
  "2 Timothy", "Titus", "Philemon", "Hebrews", "James", "1 Peter", "2 Peter",
  "1 John", "2 John", "3 John", "Jude", "Revelation",
] as const;

export interface BibleVersionManifest {
  id: BibleVersionId;
  abbreviation: string;
  name: string;
  edition: string;
  language: string;
  canon: string;
  license: string;
  attribution: string;
  sourceUrl: string;
  assetUrl: string | null;
  sizeBytes: number | null;
  editionHash: string;
  packageHash: string | null;
  bookCount: number;
  chapterCount: number;
  verseCount: number | null;
  downloadable: boolean;
  bundled: boolean;
  verseLabels?: Record<string, string>;
  audio: BibleAudioKind;
  available?: boolean;
  availabilityMessage?: string;
}

export interface ScriptureRefRange {
  book: string;
  bookIndex: number;
  chapter: number;
  verseStart: number;
  verseEnd: number;
}

export interface ScripturePoint {
  book: string;
  bookIndex: number;
  chapter: number;
  verse: number;
}

export interface ScripturePassageRange {
  start: ScripturePoint;
  end: ScripturePoint;
}

export interface ScriptureSelection {
  versionId: BibleVersionId;
  editionHash: string;
  ranges: ScriptureRefRange[];
}

const scriptureDashPattern = /[\u2010-\u2015\u2212]/g;

export function normalizeScriptureReferenceInput(input: string): string {
  return input.trim().replace(scriptureDashPattern, "-").replace(/\s+/g, " ");
}

export function parseScripturePassageRange(input: string): ScripturePassageRange | null {
  const match = normalizeScriptureReferenceInput(input).match(
    /^((?:[1-3]\s+)?[A-Za-z]+(?:\s+(?:of\s+)?[A-Za-z]+)*)\s+(\d+):(\d+)(?:-(?:(\d+):)?(\d+))?$/i,
  );
  if (!match) return null;
  const bookIndex = bibleBooks.findIndex(
    (book) => book.toLowerCase() === match[1]?.toLowerCase(),
  );
  if (bookIndex < 0) return null;
  const chapter = Number(match[2]);
  const verse = Number(match[3]);
  const endChapter = match[4] ? Number(match[4]) : chapter;
  const endVerse = match[5] ? Number(match[5]) : verse;
  if (![chapter, verse, endChapter, endVerse].every(Number.isSafeInteger)) return null;
  const book = bibleBooks[bookIndex]!;
  return {
    start: { book, bookIndex, chapter, verse },
    end: { book, bookIndex, chapter: endChapter, verse: endVerse },
  };
}

export function scripturePassageLabel(range: ScripturePassageRange): string {
  const start = `${range.start.book} ${range.start.chapter}:${range.start.verse}`;
  const end =
    range.end.chapter === range.start.chapter
      ? `${range.end.verse}`
      : `${range.end.chapter}:${range.end.verse}`;
  return range.start.chapter === range.end.chapter && range.start.verse === range.end.verse
    ? start
    : `${start}\u2013${end}`;
}

export function expandScripturePassageRange(
  range: ScripturePassageRange,
  chapterVerseCounts: readonly number[],
  limits: { maxChapters?: number; maxVerses?: number } = {},
): { ranges: ScriptureRefRange[]; verseCount: number; label: string } {
  if (
    range.start.bookIndex !== range.end.bookIndex ||
    range.start.book.toLowerCase() !== range.end.book.toLowerCase()
  ) {
    throw new Error("The primary passage must stay within one Bible book.");
  }
  if (
    range.start.chapter < 1 ||
    range.end.chapter < range.start.chapter ||
    range.end.chapter > chapterVerseCounts.length
  ) {
    throw new Error("Choose an existing chapter range in canonical order.");
  }
  const chapterCount = range.end.chapter - range.start.chapter + 1;
  if (chapterCount > (limits.maxChapters ?? 20)) {
    throw new Error("The primary passage may span up to 20 chapters.");
  }
  const ranges: ScriptureRefRange[] = [];
  let verseCount = 0;
  for (let chapter = range.start.chapter; chapter <= range.end.chapter; chapter += 1) {
    const chapterVerses = chapterVerseCounts[chapter - 1] ?? 0;
    const verseStart = chapter === range.start.chapter ? range.start.verse : 1;
    const verseEnd = chapter === range.end.chapter ? range.end.verse : chapterVerses;
    if (
      chapterVerses < 1 ||
      verseStart < 1 ||
      verseEnd < verseStart ||
      verseEnd > chapterVerses
    ) {
      throw new Error(`The selected verses do not exist in ${range.start.book} ${chapter}.`);
    }
    verseCount += verseEnd - verseStart + 1;
    if (verseCount > (limits.maxVerses ?? 100)) {
      throw new Error("The primary passage may contain up to 100 verses.");
    }
    ranges.push({
      book: range.start.book,
      bookIndex: range.start.bookIndex,
      chapter,
      verseStart,
      verseEnd,
    });
  }
  return { ranges, verseCount, label: scripturePassageLabel(range) };
}

export interface WordTiming {
  word: string;
  verse: number;
  startMs: number;
  endMs: number;
}

export interface BibleAudioSegment {
  id: string;
  verseStart: number;
  verseEnd: number;
  text: string;
  audioUrl: string;
  durationMs: number | null;
  words: WordTiming[];
}

export interface BibleAudioManifest {
  versionId: BibleVersionId;
  editionHash: string;
  book: string;
  chapter: number;
  status: "ready" | "preparing" | "unavailable" | "failed";
  source: "public-domain" | "elevenlabs" | "browser";
  attribution: string | null;
  segments: BibleAudioSegment[];
  jobId?: string;
  error?: string;
}

export interface BibleAudioJob {
  id: string;
  versionId: BibleVersionId;
  book: string;
  chapter: number;
  status: "queued" | "generating" | "ready" | "failed";
  completedSegments: number;
  totalSegments: number;
}

interface GeneratedManifest {
  versions: Array<Omit<BibleVersionManifest, "id"> & { id: string }>;
}

// Later entries win: holybooks is the preferred source for overlapping editions.
const generated = { versions: [...publicDomainManifest.versions, ...holybooksManifest.versions] } as GeneratedManifest;
const generatedById = new Map(generated.versions.map((version) => [version.id, version]));

export const defaultBibleVersionId: BibleVersionId = "kjv";

export const bibleVersionCatalog: BibleVersionManifest[] = bibleVersionIds.map((id) => {
  const version = generatedById.get(id);
  if (!version) throw new Error(`Generated Bible manifest is missing ${id}.`);
  return { ...version, id, available: true };
});

export function isBibleVersionId(value: string): value is BibleVersionId {
  return bibleVersionIds.includes(value as BibleVersionId);
}

export function bibleVersion(id: BibleVersionId): BibleVersionManifest {
  const version = bibleVersionCatalog.find((candidate) => candidate.id === id);
  if (!version) throw new Error(`Unknown Bible version: ${id}`);
  return version;
}

export function normalizeScriptureRanges(ranges: ScriptureRefRange[]): ScriptureRefRange[] {
  const sorted = ranges
    .map((range) => ({
      ...range,
      verseStart: Math.min(range.verseStart, range.verseEnd),
      verseEnd: Math.max(range.verseStart, range.verseEnd),
    }))
    .sort(
      (left, right) =>
        left.bookIndex - right.bookIndex ||
        left.chapter - right.chapter ||
        left.verseStart - right.verseStart,
    );
  const normalized: ScriptureRefRange[] = [];
  for (const range of sorted) {
    const previous = normalized.at(-1);
    if (
      previous &&
      previous.bookIndex === range.bookIndex &&
      previous.chapter === range.chapter &&
      range.verseStart <= previous.verseEnd + 1
    ) {
      previous.verseEnd = Math.max(previous.verseEnd, range.verseEnd);
    } else {
      normalized.push({ ...range });
    }
  }
  return normalized;
}

export function scriptureRangeLabel(range: ScriptureRefRange): string {
  return `${range.book} ${range.chapter}:${range.verseStart}${
    range.verseEnd === range.verseStart ? "" : `–${range.verseEnd}`
  }`;
}

export function scriptureSelectionVerseCount(ranges: ScriptureRefRange[]): number {
  return normalizeScriptureRanges(ranges).reduce(
    (total, range) => total + range.verseEnd - range.verseStart + 1,
    0,
  );
}

export function scriptureRangeContains(
  ranges: ScriptureRefRange[],
  bookIndex: number,
  chapter: number,
  verse: number,
): boolean {
  return ranges.some(
    (range) =>
      range.bookIndex === bookIndex &&
      range.chapter === chapter &&
      verse >= range.verseStart &&
      verse <= range.verseEnd,
  );
}

export function toggleScriptureVerse(
  ranges: ScriptureRefRange[],
  reference: Omit<ScriptureRefRange, "verseEnd"> & { verseEnd?: number },
): ScriptureRefRange[] {
  const verse = reference.verseStart;
  const expanded = new Map<string, ScriptureRefRange>();
  for (const range of normalizeScriptureRanges(ranges)) {
    for (let candidate = range.verseStart; candidate <= range.verseEnd; candidate += 1) {
      expanded.set(`${range.bookIndex}:${range.chapter}:${candidate}`, {
        ...range,
        verseStart: candidate,
        verseEnd: candidate,
      });
    }
  }
  const key = `${reference.bookIndex}:${reference.chapter}:${verse}`;
  if (expanded.has(key)) expanded.delete(key);
  else {
    expanded.set(key, {
      book: reference.book,
      bookIndex: reference.bookIndex,
      chapter: reference.chapter,
      verseStart: verse,
      verseEnd: verse,
    });
  }
  return normalizeScriptureRanges([...expanded.values()]);
}
